// Глобальный репортер клиентских JS-ошибок (веб-фронт + десктоп-EXE) в бэкенд-ловец.
//
// Навешивается один раз через initClientErrorReporter() (свяжет orchestrator в index/App):
//   - window 'error'              — глобальные необработанные JS-ошибки
//   - window 'unhandledrejection' — необработанные rejected-промисы
// ErrorBoundary дополнительно зовёт reportClientError() из componentDidCatch.
//
// Контракт бэка: POST ${BACKEND}/api/error-watch/client-error/
//   body {message, stack, source, url, release} → 200/201, требует сессии (401/403 глотаем).
//
// Анти-шторм (обязателен, иначе цикл ошибок завалит бэк):
//   - дедуп по ключу (message + первая строка stack): один и тот же ключ не чаще раза в ~60с;
//   - глобальный лимит на сессию — не более MAX_REPORTS_PER_SESSION отправок;
//   - сбой самой отправки молча глотаем (репортер не должен падать/рекурситься);
//   - собственные запросы к /api/error-watch/ отфильтровываем (петля).
import { BACKEND } from '../config';
import { csrfFetch } from '../utils/csrfFetch';

const ENDPOINT = `${BACKEND}/api/error-watch/client-error/`;
const DEDUP_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_SESSION = 20;

type ClientErrorSource = 'frontend' | 'exe';

interface ClientErrorPayload {
  message: string;
  stack: string;
  source: ClientErrorSource;
  url: string;
  release: string;
}

// Последнее время отправки по ключу (message + первая строка stack).
const lastSentByKey = new Map<string, number>();
let sentThisSession = 0;
let installed = false;
// Версию EXE тянем один раз асинхронно, чтобы не ждать её на каждой ошибке.
let cachedRelease = '';

function detectSource(): ClientErrorSource {
  return window.desktopApp ? 'exe' : 'frontend';
}

function extractMessageAndStack(err: unknown): { message: string; stack: string } {
  if (err instanceof Error) {
    return { message: err.message || String(err), stack: err.stack || '' };
  }
  if (err && typeof err === 'object') {
    const obj = err as { message?: unknown; stack?: unknown };
    const message = typeof obj.message === 'string' ? obj.message : safeStringify(err);
    const stack = typeof obj.stack === 'string' ? obj.stack : '';
    return { message, stack };
  }
  return { message: String(err), stack: '' };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// Ключ дедупа: сообщение + первая строка стека (обрезаны для стабильности).
function dedupKey(message: string, stack: string): string {
  const firstStackLine = stack.split('\n', 1)[0] || '';
  return `${message.slice(0, 200)}::${firstStackLine.slice(0, 200)}`;
}

// Наши же запросы к ловцу не репортим, иначе получим петлю.
function isSelfReport(message: string, stack: string): boolean {
  const haystack = `${message}\n${stack}`;
  return haystack.includes('error-watch') || haystack.includes('client-error');
}

function loadRelease(): void {
  const getVersion = window.desktopApp?.getVersion;
  if (getVersion) {
    // Не ждём: если версия придёт — попадёт в последующие репорты.
    getVersion()
      .then((v) => {
        if (typeof v === 'string') cachedRelease = v;
      })
      .catch(() => {
        /* версия недоступна — release останется пустым */
      });
  }
}

export function reportClientError(err: unknown, extra?: { source?: ClientErrorSource }): void {
  try {
    if (sentThisSession >= MAX_REPORTS_PER_SESSION) return;

    const { message, stack } = extractMessageAndStack(err);
    if (!message) return;
    if (isSelfReport(message, stack)) return;

    const key = dedupKey(message, stack);
    const now = Date.now();
    const last = lastSentByKey.get(key);
    if (last !== undefined && now - last < DEDUP_WINDOW_MS) return;
    lastSentByKey.set(key, now);
    sentThisSession += 1;

    const payload: ClientErrorPayload = {
      message: message.slice(0, 2000),
      stack: stack.slice(0, 8000),
      source: extra?.source ?? detectSource(),
      url: window.location.href,
      release: cachedRelease,
    };

    // Ошибку отправки молча глотаем — репортер не имеет права падать/рекурситься.
    csrfFetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      /* сеть/401/403 — не наша забота, репортер тихий */
    });
  } catch {
    /* никакой сбой внутри репортера не должен всплывать наружу */
  }
}

export function initClientErrorReporter(): void {
  if (installed) return;
  installed = true;

  loadRelease();

  window.addEventListener('error', (event: ErrorEvent) => {
    // event.error — реальный Error (когда доступен); иначе собираем из message.
    reportClientError(event.error ?? { message: event.message });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportClientError(event.reason);
  });
}
